import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent,
  MouseSensor, TouchSensor, pointerWithin, rectIntersection,
  CollisionDetection, useSensor, useSensors
} from '@dnd-kit/core';
import { useMemo, useState } from 'react';
import { KanbanColumn } from './KanbanColumn';
import { TaskCard } from './TaskCard';
import { useCatalogs, useMoveTask, useTasks } from '@/hooks/useTasks';
import { useEffectiveCapacidad } from '@/hooks/useCapacidades';
import { useAppStore } from '@/store/appStore';
import { applyFilters } from '@/components/filters/FilterPanel';
import type { GroupBy, Task, EffectiveCapacidad } from '@/types';

interface ColumnDef { id: number; name: string; color: string; }

function getColumns(groupBy: GroupBy, catalogs: ReturnType<typeof useCatalogs>['data']): ColumnDef[] {
  if (!catalogs) return [];
  switch (groupBy) {
    case 'status':
      return catalogs.statuses
        .slice()
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
        .map(s => ({ id: s.idEst, name: s.estado || '', color: s.colorHex || '#888' }));
    case 'assignee':
      return catalogs.users.map(u => ({
        id: u.idCol, name: u.nombreCompleto || u.nombre || '', color: '#747B86'
      }));
    case 'project':
      return catalogs.projects
        .slice()
        .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
        .map(p => ({ id: p.idProyecto, name: p.nombre, color: p.colorHex || '#888' }));
  }
}

function groupTasks(tasks: Task[], groupBy: GroupBy): Map<number, Task[]> {
  const map = new Map<number, Task[]>();
  for (const t of tasks) {
    const key = groupBy === 'status'   ? (t.idEstado ?? 0)
              : groupBy === 'assignee' ? (t.responsable ?? 0)
                                       : (t.idProyecto ?? 0);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(t);
  }
  for (const [k, list] of map) {
    list.sort((a, b) => {
      const pa = groupBy === 'status' ? a.positionByStatus
               : groupBy === 'assignee' ? a.positionByAssignee
                                        : a.positionByProject;
      const pb = groupBy === 'status' ? b.positionByStatus
               : groupBy === 'assignee' ? b.positionByAssignee
                                        : b.positionByProject;
      return pa - pb;
    });
    map.set(k, list);
  }
  return map;
}
const collisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return rectIntersection(args);
};
export function KanbanBoard() {
  const groupBy = useAppStore(s => s.groupBy);
  const search = useAppStore(s => s.search);
  const filters = useAppStore(s => s.filters);
  const selectTask = useAppStore(s => s.selectTask);
  const setPendingStatusChange = useAppStore(s => s.setPendingStatusChange);
  const { data: catalogs } = useCatalogs();
  const { data: tasks = [], isLoading } = useTasks(groupBy, search);
  const moveTask = useMoveTask();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const filteredTasks = useMemo(() => applyFilters(tasks, filters), [tasks, filters]);
  const columns = useMemo(() => getColumns(groupBy, catalogs), [groupBy, catalogs]);
  const grouped = useMemo(() => groupTasks(filteredTasks, groupBy), [filteredTasks, groupBy]);

  // Indicador de capacidad: solo en vista por responsable. Si no hay sprint
  // elegido en el filtro, usamos el semanal vigente como fallback para que la
  // barra siempre se vea (igual que en planning). En las otras vistas (status,
  // project) las horas no se distribuyen por persona.
  const today = new Date().toISOString().slice(0, 10);
  const vigenteId = useMemo(
    () => catalogs?.sprints.find(
      (s) => s.tipo === 'Semanal' && s.activo
        && s.fechaInicio && s.fechaFin
        && s.fechaInicio.slice(0, 10) <= today && s.fechaFin.slice(0, 10) >= today,
    )?.idSprint ?? null,
    [catalogs?.sprints, today],
  );
  const capSprintId = groupBy === 'assignee' ? (filters.sprintId ?? vigenteId) : null;
  const showCapacidad = groupBy === 'assignee' && capSprintId !== null;
  const { data: capacidad = [] } = useEffectiveCapacidad(showCapacidad ? capSprintId : null);
  const capacidadByCol = useMemo(() => {
    const m = new Map<number, EffectiveCapacidad>();
    capacidad.forEach(c => m.set(c.idCol, c));
    return m;
  }, [capacidad]);

  // Carga como revisor por persona, sobre las tareas VISIBLES (filteredTasks),
  // así coincide con lo que se ve y respeta el sprint filtrado. total = todas
  // donde es Autorizado Interno (cualquier estado); revisadas = las que ya
  // tienen fecha de autorización. La barra llena = revisadas / total.
  const revisorByCol = useMemo(() => {
    const m = new Map<number, { revisadas: number; total: number }>();
    if (groupBy !== 'assignee') return m;
    filteredTasks.forEach((t) => {
      if (t.idAutorizadoInt == null) return;
      const e = m.get(t.idAutorizadoInt) ?? { revisadas: 0, total: 0 };
      e.total++;
      if (t.fechaAutorizadoInt != null) e.revisadas++;
      m.set(t.idAutorizadoInt, e);
    });
    return m;
  }, [filteredTasks, groupBy]);

  function handleDragStart(e: DragStartEvent) {
    const task = e.active.data.current?.task as Task | undefined;
    if (task) setActiveTask(task);
  }

  function handleDragEnd(e: DragEndEvent) {
    setActiveTask(null);
    const { active, over } = e;
    if (!over) return;

    const activeTaskData = active.data.current?.task as Task | undefined;
    if (!activeTaskData) return;

    let targetGroupId: number | null = null;
    let overTask: Task | null = null;

    if (over.data.current?.type === 'column') {
      targetGroupId = over.data.current.groupId;
    } else if (over.data.current?.type === 'task') {
      overTask = over.data.current.task as Task;
      targetGroupId = groupBy === 'status'   ? (overTask.idEstado ?? 0)
                    : groupBy === 'assignee' ? (overTask.responsable ?? 0)
                                              : (overTask.idProyecto ?? 0);
    }
    if (targetGroupId === null) return;

    const tasksInTarget = grouped.get(targetGroupId) ?? [];
    let beforePos: number | undefined;
    let afterPos: number | undefined;

    if (overTask && overTask.idTareaArq !== activeTaskData.idTareaArq) {
      const idx = tasksInTarget.findIndex(t => t.idTareaArq === overTask!.idTareaArq);
      const overPos = groupBy === 'status' ? overTask.positionByStatus
                    : groupBy === 'assignee' ? overTask.positionByAssignee
                                              : overTask.positionByProject;
      const prev = tasksInTarget[idx - 1];
      const prevPos = prev ? (groupBy === 'status' ? prev.positionByStatus
                            : groupBy === 'assignee' ? prev.positionByAssignee
                                                     : prev.positionByProject) : undefined;
      beforePos = prevPos;
      afterPos = overPos;
    } else if (tasksInTarget.length > 0) {
      const last = tasksInTarget[tasksInTarget.length - 1];
      beforePos = groupBy === 'status' ? last.positionByStatus
                : groupBy === 'assignee' ? last.positionByAssignee
                                         : last.positionByProject;
    }

    // Interceptar moves a un estado que requiere FechaCompletado cuando la tarea
    // no la tiene: en vez de mutar, abrimos el modal con el estado nuevo
    // pre-seleccionado para que el usuario complete la fecha y guarde. Si cancela,
    // la tarea queda en su columna original (no se llamó al backend).
    if (groupBy === 'status') {
      const targetStatus = catalogs?.statuses.find(s => s.idEst === targetGroupId);
      const taskHasFechaCompletado = !!activeTaskData.fechaCompletado;
      if (targetStatus?.requiresFechaCompletado && !taskHasFechaCompletado) {
        setPendingStatusChange({
          taskId: activeTaskData.idTareaArq,
          newStatusId: targetGroupId,
        });
        selectTask(activeTaskData.idTareaArq);
        return;
      }
    }

    // Caso normal. Si el move sale de un estado que requiere fecha, el backend
    // la limpia y re-deriva los Tg* automáticamente.
    moveTask.mutate({
      id: activeTaskData.idTareaArq,
      dto: {
        groupBy, targetGroupId,
        beforePosition: beforePos, afterPosition: afterPos,
        rowVersion: activeTaskData.rowVersion ?? undefined
      }
    });
  }

  if (isLoading) return <div className="p-8 text-center text-stone-500">Cargando…</div>;

  return (
<DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-2 p-3 overflow-x-auto h-full">
        {columns.map(col => (
          <KanbanColumn
            key={col.id}
            groupId={col.id}
            groupName={col.name}
            groupColor={col.color}
            tasks={grouped.get(col.id) ?? []}
            capacidad={showCapacidad ? capacidadByCol.get(col.id) : undefined}
            revisor={groupBy === 'assignee' ? revisorByCol.get(col.id) : undefined}
          />
        ))}
      </div>
      <DragOverlay>{activeTask ? <TaskCard task={activeTask} /> : null}</DragOverlay>
    </DndContext>
  );
}